/**
 * HEIGHT DATUM SETTINGS (2026-10-07, Matt — Newport Beach §20.30.050 / §20.30.060, plus a
 * private HOA limit) — DERIVED, never typed in.
 *
 * Matt: "allowing me to change the height restriction manually as a setting in the site plan
 * is an avenue for user error ... We can make the return on that investment of time
 * cumulative if we codify those requirements into the system." So the limits, the chimney
 * allowance and the grade method all come from the DEVELOPMENT STANDARDS the project's parcel
 * research resolves to (database/286, api/_developmentStandards.js): the zone's (or special
 * overlay's) height rule, and the HOA's rule for this parcel's lot. ModelerPage mirrors the
 * resolved rules into the doc (`siteStandards`, the projectInfo / siteJurisdiction
 * mirror-at-source pattern) so sheets render synchronously and offline. There is NO
 * per-project override (Matt): a wrong rule is fixed on the zone or HOA, for every project.
 *
 * Grade method: 'newport' rules run the code's own 5% test ('auto'); a parcel in a flood
 * hazard area measures from top of slab at the rule's minimum elevation (§20.30.060.B.3,
 * REAL-WORLD feet, converted through the site datum at compute time). 'flat' / 'slope'
 * remain as engine methods (the 5% test picks between them).
 */
import { maps, transact, type ModelDoc } from './doc'

export type HeightDatumMethod = 'auto' | 'flat' | 'slope' | 'slab'

/** the city's height rule, as resolved for this parcel */
export interface CityHeightRule {
  sourceName: string
  label: string
  citation: string
  gradeMethod: 'newport'
  flatLimitFt: number
  slopedLimitFt: number
  chimneyAllowanceFt: number
  floodSlabMinElevFt: number
}
/** the HOA's height rule for this parcel's lot */
export interface HoaHeightRule {
  sourceName: string
  label: string
  citation: string
  limitFt: number
  /** what a chimney may rise over the HOA limit; null = the CC&Rs do not limit chimneys */
  chimneyAllowanceFt: number | null
  tract?: string | null
  lot?: number | null
}
/** everything the parcel's research resolves to (mirrored, never edited here) */
export interface SiteStandards {
  parcel: { tract: string | null; lot: number | null; floodZone: boolean } | null
  height: { city: CityHeightRule | null; hoa: HoaHeightRule | null; problems: string[] }
  /** the jurisdiction's CONFIRMED demolition rules (database/296); absent on older mirrors */
  demolition?: DemolitionRule[]
  /** the required-parking rule (database/313): overlay → zone → jurisdiction; absent on older mirrors */
  parking?: ParkingRule | null
  /** the required STALL SIZES by stall type (database/281: overlay → zone → jurisdiction →
   *  firm), for the site plan's dimensioned stalls; absent on older mirrors */
  stalls?: Record<string, StallSize> | null
}

/** one stall type's required size and where it came from */
export interface StallSize {
  widthFt: number
  lengthFt: number
  aisleFt: number | null
  codeReference: string
  source: 'zone' | 'jurisdiction' | 'firm'
  sourceId: string | null
}

/** how many spaces a home needs, by its size (G001's Parking row, titleSheetModel.js) */
export interface ParkingRule {
  sourceName: string
  label: string
  citation: string
  confirmed: boolean
  covered: number
  uncovered: number
  /** above this many square feet of `areaBasis`, the larger counts apply (null = no size step) */
  largerOverSf: number | null
  largerCovered: number | null
  largerUncovered: number | null
  areaBasis: 'living' | 'living_storage'
}

/** a city's "major remodel" rule — when a remodel counts as new construction (round 11) */
export interface DemolitionRule {
  sourceName: string
  label: string
  citation: string
  thresholdPct: number
  components: ('extWalls' | 'roof' | 'foundation' | 'floor')[]
  /** 'perComponent' — any one component at/over the threshold; 'combined' — their shares averaged */
  counting: 'perComponent' | 'combined'
  /** new floor area against existing that also triggers, percent */
  additionsPct: number | null
  /** what the rule counts that the model cannot see */
  notMeasured: string[]
}

const KEY = 'siteStandards'

export function getSiteStandards(model: ModelDoc): SiteStandards | null {
  const v = maps(model).settings.get(KEY) as SiteStandards | undefined
  return v && typeof v === 'object' && v.height ? v : null
}

/** the mirror's write (ModelerPage, from the parcel research). One transact = one undo step. */
export function setSiteStandards(model: ModelDoc, standards: SiteStandards | null): void {
  transact(model, () => {
    if (standards) maps(model).settings.set(KEY, standards)
    else maps(model).settings.delete(KEY)
  })
}

export interface HeightDatumSettings {
  /** a city height rule is on file: nothing is checked without one */
  ready: boolean
  method: HeightDatumMethod
  /** max height for flat roofs, guardrails and parapets, feet above the established grade */
  flatLimitFt: number
  /** max height for sloped roofs (to the highest ridge), feet */
  slopedLimitFt: number
  /** what a chimney may rise over the limit */
  chimneyAllowanceFt: number
  /** flood-zone top of slab, REAL-WORLD feet (method 'slab' only) */
  slabRealElevFt: number
  /** the city rule's source and citation, for the drawings */
  sourceName: string
  citation: string
  /** the HOA limit above the SAME established grade; null = none */
  hoaLimitFt: number | null
  /** null = chimneys are not held to the HOA limit (the CC&Rs say nothing about them) */
  hoaChimneyAllowanceFt: number | null
  hoaName: string
  hoaLabel: string
  hoaCitation: string
  /** why a rule could not be resolved, in plain words, for the drawings */
  problems: string[]
}

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

export function getHeightDatumSettings(model: ModelDoc): HeightDatumSettings {
  const st = getSiteStandards(model)
  const city = st?.height.city ?? null
  const hoa = st?.height.hoa ?? null
  const flat = finite(city?.flatLimitFt), sloped = finite(city?.slopedLimitFt)
  const ready = flat !== null && sloped !== null
  const problems = [...(st?.height.problems ?? [])]
  if (!st) problems.push('The development standards have not been read from the parcel research yet.')
  return {
    ready,
    method: st?.parcel?.floodZone ? 'slab' : 'auto',
    flatLimitFt: flat ?? 0,
    slopedLimitFt: sloped ?? 0,
    chimneyAllowanceFt: finite(city?.chimneyAllowanceFt) ?? 0,
    slabRealElevFt: finite(city?.floodSlabMinElevFt) ?? 9,
    sourceName: city?.sourceName ?? '',
    citation: city?.citation ?? '',
    hoaLimitFt: ready ? finite(hoa?.limitFt) : null,
    hoaChimneyAllowanceFt: finite(hoa?.chimneyAllowanceFt),
    hoaName: hoa?.sourceName ?? '',
    hoaLabel: hoa?.label ?? '',
    hoaCitation: hoa?.citation ?? '',
    problems,
  }
}
